import type { CloneJob } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as cloneJobsApi from "@/lib/api/clone-jobs";

export const CLONE_JOBS_KEY = ["clone-jobs"] as const;

/** While a copy is queued or running: the progress counter moves. */
const ACTIVE_POLL_MS = 1500;
/** Otherwise: picks up copies other designers start. */
const IDLE_POLL_MS = 30_000;

/** One empty list, so a render with no data does not hand out a new array. */
const NO_JOBS: CloneJob[] = [];

export const isCloneJobActive = (job: CloneJob) =>
  job.status === "pending" || job.status === "processing";

/**
 * QL-334 — the tenant's background copies. Polls fast while one is queued or
 * running and slowly otherwise, so a copy someone else starts shows up too.
 * It keeps polling in a hidden tab: a copy that finishes there still refreshes
 * the lists and toasts. Starting a copy invalidates the key, which picks up
 * the new job at once.
 */
export function useCloneJobs(enabled = true) {
  const { data } = useQuery({
    queryKey: CLONE_JOBS_KEY,
    queryFn: cloneJobsApi.listCloneJobs,
    enabled,
    refetchInterval: (q) => (q.state.data?.some(isCloneJobActive) ? ACTIVE_POLL_MS : IDLE_POLL_MS),
    refetchIntervalInBackground: true,
  });
  return { jobs: data ?? NO_JOBS };
}

/** Cancels a queued copy or dismisses a failed one; only the person who started it may. */
export function useDismissCloneJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cloneJobsApi.dismissCloneJob,
    // A refused cancel (the copy just started) also refreshes the row.
    onSettled: () => qc.invalidateQueries({ queryKey: CLONE_JOBS_KEY }),
  });
}

/** The copies of one kind a list shows as rows: every job until its real row replaces it. */
export function useListedCloneJobs(kind: CloneJob["kind"]) {
  return useCloneJobs().jobs.filter((job) => job.kind === kind && job.status !== "done");
}
